/**
 * MockDetector: React-Specific Mock Data Patterns
 * 
 * Detects React-specific mock data patterns including:
 * - useState with hardcoded arrays/objects
 * - useEffect with mock data
 * - Component props with mock values
 * - Map functions with hardcoded data
 */

import { MockViolation } from '../types/mockdetector.types';
import { calculateViolationConfidence } from './confidenceScoring';
import { enhanceViolationWithFix } from './contextualAutoFix';

export interface ReactPattern {
  name: string;
  pattern: RegExp;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  autoFix: (match: RegExpMatchArray, code: string) => string | null;
}

export const REACT_MOCK_PATTERNS: ReactPattern[] = [
  {
    name: 'useState_hardcoded_array',
    pattern: /useState\s*(?:<[^>]+>)?\s*\(\s*\[\s*\{/g,
    severity: 'critical',
    description: 'useState initialized with hardcoded array of objects (likely mock data)',
    autoFix: (_match) => {
      return `// Replace with database fetch:
const [data, setData] = useState([]);

useEffect(() => {
  const fetchData = async () => {
    const { data, error } = await supabase
      .from('your_table')
      .select('*');
    if (data) setData(data);
  };
  fetchData();
}, []);`;
    }
  },
  
  {
    name: 'useEffect_fake_response',
    pattern: /useEffect\s*\([^)]*\)\s*=>\s*\{[^}]*(?:fakeResponse|mockData|testData|fakeAPI|fakeFetch)\s*=/g,
    severity: 'critical',
    description: 'useEffect contains fake/mock data assignment instead of real API call',
    autoFix: (_match) => {
      return `// Replace fake data with real Supabase query:
useEffect(() => {
  const fetchRealData = async () => {
    const { data, error } = await supabase.from('your_table').select('*');
    if (data) setYourState(data);
  };
  fetchRealData();
}, []);`;
    }
  },
  
  {
    name: 'suspicious_function_names',
    pattern: /(?:const|function)\s+(?:handle|validate|fetch|get)?(?:Mock|Test|Fake|Demo|Sample)[A-Z]\w*\s*=/g,
    severity: 'high',
    description: 'Function name contains mock/test/fake - likely temporary code',
    autoFix: (match) => {
      const funcName = match[0].match(/(?:Mock|Test|Fake|Demo|Sample)[A-Z]\w*/)?.[0] || '';
      const realName = funcName.replace(/Mock|Test|Fake|Demo|Sample/gi, '');
      return `// Rename function: ${funcName} → ${realName}\n// Remove mock/test prefix`;
    }
  },
  
  {
    name: 'test_array_variables',
    pattern: /const\s+(testUsers|mockUsers|fakeData|demoData|sampleData|testData|mockItems|fakeProducts)\s*=\s*\[/g,
    severity: 'high',
    description: 'Variable name suggests test/mock data array',
    autoFix: (match) => {
      const varName = match[1];
      const realName = varName.replace(/test|mock|fake|demo|sample/gi, '');
      return `// Replace: const ${varName} = [...]\n// With: Fetch real data and store in const ${realName}`;
    }
  },
  
  // useState_hardcoded_number pattern REMOVED - too aggressive
  // Legitimate uses: progress bars (useState(13)), counters (useState(0)), pagination (useState(1))
  // Only flag if it's clearly mock data (thousands/millions), not configuration values
  
  {
    name: 'map_with_array_literal',
    pattern: /\[\s*\{[^}]*\}\s*(?:,\s*\{[^}]*\}){2,}\s*\]\.map\s*\(/g,
    severity: 'high',
    description: 'Array literal with objects directly in .map() call (hardcoded data)',
    autoFix: (_match) => {
      return `// Replace with state-based data:
{data.map((item) => (
  // Your JSX here
))}

// Fetch data with:
useEffect(() => {
  const loadData = async () => {
    const { data } = await supabase.from('your_table').select('*');
    setData(data || []);
  };
  loadData();
}, []);`;
    }
  },
  
  {
    name: 'array_fill_mock',
    pattern: /Array\s*\(\s*\d+\s*\)\.fill\s*\(\s*\{[^}]*\}\s*\)/g,
    severity: 'critical',
    description: 'Array.fill() with mock objects (generates fake data)',
    autoFix: (_match) => {
      return `// ❌ Array.fill() creates identical mock data!
// ✅ Replace with real database query:
const { data } = await supabase
  .from('your_table')
  .select('*')
  .limit(10); // Adjust limit as needed`;
    }
  },
  
  // Math.random() pattern REMOVED - too many false positives (UI animations, effects)
  
  {
    name: 'test_email_pattern',
    pattern: /(test|demo|sample|example)@(test|demo|example)\.(com|org|net)/gi,
    severity: 'high',
    description: 'Test email address found in code',
    autoFix: (_match) => {
      return `// ❌ Test email detected!
// ✅ Use real user email from auth:
const { data: { user } } = await supabase.auth.getUser();
const email = user?.email;`;
    }
  },
  
  {
    name: 'sequential_user_mock',
    pattern: /(user|name|email):\s*['"`](User|Name|Email)\s*\$\{i\$\}['"`]/gi,
    severity: 'critical',
    description: 'Sequential user generation pattern (User ${i}, Name ${i}, etc.)',
    autoFix: (_match) => {
      return `// ❌ Sequential mock users detected!
// ✅ Fetch real users from database:
const { data: users } = await supabase
  .from('profiles')
  .select('id, name, email')
  .order('created_at', { ascending: false })
  .limit(10);`;
    }
  },
  
  // lorem_ipsum pattern now checks context to avoid flagging comments and detection code
  {
    name: 'lorem_ipsum',
    pattern: /(['"`]).*?(lorem\s+ipsum|dolor\s+sit\s+amet).*?\1/gi,
    severity: 'medium',
    description: 'Lorem Ipsum placeholder text found in string',
    autoFix: (_match) => {
      return `// ❌ Lorem Ipsum placeholder text!
// ✅ Use real content from your database:
const { data } = await supabase
  .from('content')
  .select('text')
  .eq('type', 'description')
  .single();`;
    }
  },
  
  {
    name: 'date_now_mock',
    pattern: /new\s+Date\s*\(\s*['"`]\d{4}-\d{2}-\d{2}['"`]\s*\)/g,
    severity: 'low',
    description: 'Hardcoded date string (consider using real timestamps)',
    autoFix: (_match) => {
      return `// Consider using real timestamps from database:
// created_at, updated_at columns are automatically set by Supabase`;
    }
  },
  
  {
    name: 'useEffect_with_mock_data',
    pattern: /useEffect\s*\(\s*\(\s*\)\s*=>\s*\{\s*set\w+\s*\(\s*\[[^\]]{50,}\]\s*\)/g,
    severity: 'high',
    description: 'useEffect setting state with inline mock data array',
    autoFix: (_match) => {
      return `// Replace mock data with real API/database call:
useEffect(() => {
  const fetchData = async () => {
    try {
      const { data, error } = await supabase
        .from('your_table')
        .select('*');
      
      if (error) throw error;
      setData(data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  
  fetchData();
}, []);`;
    }
  },
  
  {
    name: 'hardcoded_api_response',
    pattern: /const\s+\w+\s*=\s*\{\s*data:\s*\[[^\]]{50,}\]/g,
    severity: 'critical',
    description: 'Hardcoded API response object with data array',
    autoFix: (_match) => {
      return `// ❌ Hardcoded API response!
// ✅ Make real API call:
const response = await supabase.functions.invoke('your-function', {
  body: { /* your params */ }
});
const data = response.data;`;
    }
  },
  
  {
    name: 'fetch_mock_url',
    pattern: /(?:fetch|axios\.(?:get|post|put|delete))\s*\(\s*['"`](?:https?:\/\/)?(?:localhost|127\.0\.0\.1|example\.com|test\.com|jsonplaceholder\.typicode\.com|reqres\.in|mockapi\.io)/gi,
    severity: 'critical',
    description: 'HTTP request to mock/test endpoint (localhost, example.com, or mock API)',
    autoFix: (_match) => {
      return `// ❌ Fetch/Axios to mock URL detected!
// ✅ Replace with Supabase Edge Function:
const { data, error } = await supabase.functions.invoke('your-function', {
  body: { /* params */ }
});`;
    }
  },
  
  // console_log_mock_data pattern REMOVED - was flagging console.logs about the 
  // mock detection system itself, e.g. "Mock chat simulation disabled"
];

/**
 * Scan React code for mock data patterns
 */
export const scanReactCode = (code: string, fileName?: string): MockViolation[] => {
  const violations: MockViolation[] = [];
  const lines = code.split('\n');
  
  REACT_MOCK_PATTERNS.forEach(({ name: _name, pattern, severity, description, autoFix }) => {
    // Reset regex state
    pattern.lastIndex = 0;
    
    let match: RegExpMatchArray | null;
    while ((match = pattern.exec(code)) !== null) {
      // Find line number
      const lineNumber = code.substring(0, match.index).split('\n').length;
      const line = lines[lineNumber - 1];
      
      // Generate auto-fix suggestion
      const fixSuggestion = autoFix(match, code);
      
      const violation: MockViolation = {
        type: 'mock_data',
        severity,
        file: fileName,
        line: lineNumber,
        code: line?.trim() || match[0].trim(),
        description: `React Pattern: ${description}`,
        fix: fixSuggestion || 'Replace with real data from database or API',
        confidence: 0 // Will be calculated
      };
      
      // ✅ PHASE 3: Calculate confidence
      violation.confidence = calculateViolationConfidence(violation);
      
      // Boost confidence for React-specific patterns
      if (fileName?.includes('/components/') || fileName?.includes('/pages/')) {
        violation.confidence = Math.min(100, (violation.confidence || 50) + 20);
      }
      
      // Critical patterns get extra boost
      const patternName = REACT_MOCK_PATTERNS.find(p => p.description === description.replace('React Pattern: ', ''))?.name;
      if (patternName === 'useState_hardcoded_array' || patternName === 'useEffect_fake_response') {
        violation.confidence = Math.max(85, violation.confidence || 85);
      }
      if (patternName === 'test_array_variables') {
        violation.confidence = Math.max(85, violation.confidence || 85);
      }
      
      // ✅ PHASE 4: Enhance with contextual auto-fix (overrides the basic autoFix)
      const enhanced = enhanceViolationWithFix(violation) as MockViolation;
      
      // Lower threshold: 40% instead of 50%
      if (enhanced.confidence && enhanced.confidence >= 40) {
        violations.push(enhanced);
      }
    }
  });
  
  return violations;
};
